var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Form from "./shared/primitives/Form";
import Button from "./shared/primitives/Button";
import { createLearningTaskAnswerAction, createLearningTaskAnswerVersionAction, updateLearningTaskAnswerVersionAction, deleteLearningTaskAnswerVersionAttachmentAction, } from "slices/learningTasks";
import LearningTaskAnswerVersionComments from "./LearningTaskAnswerVersionComments";
import FlexContainer from "./shared/layout/FlexContainer";
import Paragraph from "./shared/primitives/Paragraph";
import { useAppDispatch } from "reduxStore";
import { selectAnswerLastSavedAt } from "selectors/learningTask";
import { useSelector } from "react-redux";
import Link from "./shared/primitives/Link";
import TriaphonMDXEditorWithFullscreen from "./shared/TriaphonMDXEditorWithFullscreen";
function LearningTaskAnswerVersionActionButtons(props) {
    const { withSubmit = false, hintText, disabled = false, handleSaveButtonClick } = props;
    return (React.createElement(FlexContainer, { flexDirection: "column", alignItems: "flex-start", gap: "0.5rem" },
        React.createElement(FlexContainer, { justifyContent: "flex-start", gap: "1rem", padding: "1rem 0 0 0" },
            React.createElement(Button, { onClick: handleSaveButtonClick, disabled: disabled }, "Zwischenspeichern"),
            withSubmit && (React.createElement(Button, { type: "submit", disabled: disabled }, "Einreichen"))),
        hintText && React.createElement(Paragraph, null, hintText)));
}
export default function LearningTaskAnswerVersion(props) {
    const dispatch = useAppDispatch();
    const { version, taskId, answerId: answerIdProp } = props;
    const { text = "", comments, attachments = [] } = version || {};
    const formMethods = useForm({ defaultValues: { text } });
    const [hintText, setHintText] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const fileInputRef = useRef(null);
    const { getValues: getFormValues } = formMethods;
    const answerLastSavedAt = useSelector(selectAnswerLastSavedAt);
    useEffect(() => {
        if (!answerLastSavedAt)
            return;
        const now = Date.now();
        if (now > answerLastSavedAt + 1000)
            return;
        const text = "Zwischenspeichern erfolgreich!";
        const timeout = 2000;
        setHintText(text);
        setTimeout(() => {
            setHintText(null);
        }, timeout);
    }, [answerLastSavedAt]);
    const upsertTaskAnswer = (...args_1) => __awaiter(this, [...args_1], void 0, function* (status = "draft") {
        const { text } = getFormValues();
        let answerId = answerIdProp;
        if (!answerId) {
            const response = yield dispatch(createLearningTaskAnswerAction(taskId));
            answerId = response.payload.id;
        }
        const dataForUpsert = {
            text,
            status,
            taskId,
            answerId,
            attachments: selectedFiles.length > 0 ? selectedFiles : undefined,
        };
        if (version) {
            dispatch(updateLearningTaskAnswerVersionAction(Object.assign(Object.assign({}, version), dataForUpsert)));
        }
        else {
            dispatch(createLearningTaskAnswerVersionAction(dataForUpsert));
        }
        // Clear selected files after successful upload
        setSelectedFiles([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    });
    const handleFileChange = (event) => {
        const files = event.target.files;
        if (files) {
            setSelectedFiles(Array.from(files));
        }
    };
    const handleDeleteAttachment = (attachmentId) => __awaiter(this, void 0, void 0, function* () {
        if (!version || !answerIdProp)
            return;
        if (window.confirm("Möchten Sie diese Datei wirklich löschen?")) {
            yield dispatch(deleteLearningTaskAnswerVersionAttachmentAction({
                taskId,
                answerId: answerIdProp,
                versionId: version.id,
                attachmentId,
            }));
        }
    });
    const formatFileSize = (bytes) => {
        if (bytes === 0)
            return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
    };
    const handleSaveButtonClick = (event) => {
        upsertTaskAnswer();
        event.preventDefault();
        event.stopPropagation();
    };
    const onSubmit = () => {
        upsertTaskAnswer("submitted");
    };
    const disabled = version && version.status !== "draft";
    const showHintText = (version === null || version === void 0 ? void 0 : version.status) !== "submitted";
    return (React.createElement(Form, { onSubmit: formMethods.handleSubmit(onSubmit), fullWidth: true },
        React.createElement(Controller, { name: "text", control: formMethods.control, render: ({ field }) => (React.createElement(TriaphonMDXEditorWithFullscreen, { field: field, defaultValue: text, disabled: disabled, actions: React.createElement(LearningTaskAnswerVersionActionButtons, { hintText: showHintText ? hintText : undefined, handleSaveButtonClick: handleSaveButtonClick, disabled: disabled }) })) }),
        attachments && attachments.length > 0 && (React.createElement(FlexContainer, { flexDirection: "column", gap: "0.5rem", padding: "1rem 0", alignItems: "flex-start" },
            React.createElement(Paragraph, { style: { fontWeight: "bold", marginBottom: 0 } }, "Angeh\u00E4ngte Dateien:"),
            attachments.map((attachment) => (React.createElement(FlexContainer, { key: attachment.id, alignItems: "center", gap: "0.5rem" },
                React.createElement(Link, { href: attachment.url, target: "_blank", rel: "noopener noreferrer" }, attachment.filename),
                React.createElement("span", null,
                    "(",
                    formatFileSize(attachment.byteSize),
                    ")"),
                !disabled && (React.createElement(Link, { onClick: () => handleDeleteAttachment(attachment.id), style: { cursor: "pointer" } }, "L\u00F6schen"))))))),
        React.createElement(FlexContainer, { flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start", gap: "1rem", padding: "1rem 0 0 0" }, !disabled && (React.createElement(React.Fragment, null,
            React.createElement(FlexContainer, { flexDirection: "column", alignItems: "flex-start", gap: "0.5rem" },
                React.createElement("label", { htmlFor: "file-upload", style: { fontWeight: "bold" } }, "Dateien anh\u00E4ngen:"),
                React.createElement("input", { id: "file-upload", ref: fileInputRef, type: "file", multiple: true, onChange: handleFileChange, style: { marginBottom: "0.5rem" } }),
                selectedFiles.length > 0 && (React.createElement(Paragraph, null, `Dateien ausgewählt. Bitte Zwischenspeichern um die Dateien anzuhängen.`))),
            React.createElement(LearningTaskAnswerVersionActionButtons, { hintText: showHintText ? hintText : undefined, handleSaveButtonClick: handleSaveButtonClick, disabled: disabled, withSubmit: true })))),
        comments && comments.length > 0 && React.createElement(LearningTaskAnswerVersionComments, { comments: comments })));
}
